import { publicCheckoutEstimate } from "@babackapp/private-api-client/client/use-public-checkout-estimate";
import { useMutation } from "@tanstack/react-query";
import type {
  CheckoutEstimateReturnRequestDto,
  CheckoutEstimateResultDto,
} from "@babackapp/private-api-client/generated/requests/types.gen";
import type { ReturnDraft } from "../types";

function buildCheckoutEstimatePayload(
  draft: ReturnDraft,
): CheckoutEstimateReturnRequestDto | null {
  if (!draft.address) return null;
  const address = draft.address;
  if (!address.address1) return null;

  // For direct exchanges: use the new variant ids from the draft line items.
  // For exchange-credit + rebuy (shop-now flow): no exchangeVariantId exists on line items,
  // so fall back to the rebuy cart items to estimate shipping for the new items.
  const exchangeLineItems = draft.lineItems
    .filter((li) => li.exchangeVariantId !== null)
    .map((li) => ({
      variantId: li.exchangeVariantId as string,
      quantity: li.quantity,
    }));

  const lineItems = exchangeLineItems.length > 0
    ? exchangeLineItems
    : (draft.rebuyItems ?? []).map((item) => ({
        variantId: String(item.variantId),
        quantity: item.quantity,
      }));

  if (lineItems.length === 0) return null;

  return {
    shopId: draft.shopId,
    orderId: draft.orderId,
    lineItems,
    shippingAddress: {
      address1: address.address1,
      address2: address.address2 ?? null,
      city: address.city ?? null,
      province: address.province ?? null,
      province_code: address.province_code ?? null,
      zip: address.zip ?? null,
      country_code: address.country_code ?? null,
      country: address.country ?? null,
      phone: address.phone ?? null,
      email: address.email ?? null,
      first_name: address.first_name ?? null,
      last_name: address.last_name ?? null,
      companyName: address.companyName ?? null,
    },
  };
}

export type ShippingRatesResult = CheckoutEstimateResultDto;

export function useShippingRates() {
  return useMutation<ShippingRatesResult, Error, ReturnDraft>({
    mutationFn: (draft) => {
      const payload = buildCheckoutEstimatePayload(draft);
      if (!payload) throw new Error("Draft is not ready for checkout estimate");
      return publicCheckoutEstimate(payload);
    },
  });
}
